/**
 * EL JARDÍN — lienzo de trazo, colocación y cobertura.
 * =============================================================================
 *
 * Primera capa gráfica del cotizador, y el sustrato de todas las demás: la geometría,
 * el elemento colocable y el modelo de cobertura son los mismos para riego, cámaras,
 * seguridad e iluminación —cambia la forma, no la matemática— según
 * `areas/Sistemas/Simuladores_del_Cotizador.md` §4.
 *
 * DECISIONES QUE NO SON DE GUSTO:
 *
 * · **Una unidad de SVG es un metro.** No hay factor de escala escondido en ningún
 *   lado: lo que se dibuja son metros y lo que mide el motor son metros. Un factor
 *   oculto es la forma más fácil de que un presupuesto salga mal por diez.
 *
 * · **Los huecos secos se pintan con una máscara SVG, no muestreando.** La máscara es
 *   exacta y no tiene resolución: se pinta el polígono en rojo y se le restan los
 *   círculos de riego. El muestreo del motor existe solo para el PORCENTAJE, que sí
 *   hay que contar.
 *
 * · **Se toca para poner, se arrastra para mover.** Poner con arrastre competiría con
 *   el desplazamiento de la página; mover con arrastre no, porque el dedo ya está sobre
 *   el objeto. Todo lo colocado se puede mover después —esquinas y aspersores— porque
 *   así se describe un terreno real: primero aproximado, luego se ajusta.
 *
 * · **Cada lado dice cuánto mide, mientras se traza.** Sin la cota a la vista, trazar
 *   sobre retícula es adivinar; con ella, se compara contra el flexómetro en el momento
 *   y se corrige antes de seguir. La medida se puede además escribir exacta, que es lo
 *   que separa un croquis de un levantamiento.
 */

import { useRef, useState, useMemo } from 'react'
import { aspersorPorId } from '../motor/riego.js'
 import { area, perimetro, caja, cobertura, dentro, lados } from '../motor/geometria.js'

/* ---------- Geometría de dibujo ---------- */

/** Camino del sector que riega un aspersor. Círculo completo cuando el arco es 360. */
function sector (a) {
  const arco = a.arco ?? 360
  if (arco >= 360) return null
  const ini = ((a.orientacion ?? 0) * Math.PI) / 180
  const fin = ini + (arco * Math.PI) / 180
  const x0 = a.x + a.radio * Math.cos(ini), y0 = a.y + a.radio * Math.sin(ini)
  const x1 = a.x + a.radio * Math.cos(fin), y1 = a.y + a.radio * Math.sin(fin)
  return `M${a.x} ${a.y} L${x0} ${y0} A${a.radio} ${a.radio} 0 ${arco > 180 ? 1 : 0} 1 ${x1} ${y1} Z`
}

const camino = pts => pts.length ? 'M' + pts.map(p => `${p.x} ${p.y}`).join(' L') + ' Z' : ''

/* `lados` vive en `geometria.js`: acotar un recinto es la misma operación para el jardín,
   la planta de la casa y el vano de una celosía. Se re-exporta para quien ya lo importaba
   desde aquí. */
export { lados }

export default function Jardin ({
  poligono, setPoligono,
  aspersores, setAspersores,
  toma, setToma,
  cerrado, setCerrado,
  modo,                  // 'toma' | 'trazo' | 'colocar' | 'ver'
  modeloActivo
}) {
  const svgRef = useRef(null)
  const [arrastrando, setArrastrando] = useState(null)
  /* Un arrastre termina en `mouseup`, y el navegador dispara `click` justo después. Sin
     esta bandera, soltar un aspersor en modo «colocar» colocaba OTRO encima del que se
     acababa de mover. Verificado en el navegador antes de arreglarlo. */
  const hubieArrastre = useRef(false)

  /* El cierre del polígono es EXPLÍCITO —lo decide un botón—, no deducido del número de
     esquinas. La primera versión lo derivaba de «tres o más esquinas» y el trazo se
     cerraba solo al poner la tercera: ya no dejaba agregar la cuarta, y un jardín rara
     vez es un triángulo. Se detectó recorriendo el flujo en el navegador, no leyendo el
     código: por eso el recorrido completo es parte de la verificación y no un extra. */

  /* La vista encuadra lo dibujado con dos metros y medio de aire. Sin nada dibujado, un
     lienzo de 22 x 16 m, que es un jardín residencial típico y da referencia de escala. */
  const vista = useMemo(() => {
    const pts = [...poligono, ...aspersores, ...(toma ? [toma] : [])]
    if (pts.length < 2) return { x: -2, y: -2, w: 22, h: 16 }
    const b = caja(pts)
    const m = 2.5
    return { x: b.x0 - m, y: b.y0 - m, w: (b.x1 - b.x0) + m * 2, h: (b.y1 - b.y0) + m * 2 }
  }, [poligono, aspersores, toma])

  /* Todo lo que se dibuja encima del plano —texto, trazos, tiradores— se mide en metros,
     así que hay que contrarrestar la escala para que no crezca ni encoja con el encuadre. */
  const u = vista.w / 100          // unidad de dibujo relativa al ancho de la vista
  const tipo = u * 3.4             // tamaño de la cota, legible sin tapar el plano

  /** Coordenada del dedo en metros. Pasa por la matriz del SVG: sirve con cualquier zoom. */
  const enMetros = ev => {
    const svg = svgRef.current
    const p = svg.createSVGPoint()
    const t = ev.touches?.[0] || ev.changedTouches?.[0] || ev
    p.x = t.clientX; p.y = t.clientY
    const m = p.matrixTransform(svg.getScreenCTM().inverse())
    // Media rejilla: trazar a mano libre no da precisión de centímetro, y fingirla es
    // peor que redondear a la vista. La medida exacta se escribe, no se dibuja.
    return { x: Math.round(m.x * 2) / 2, y: Math.round(m.y * 2) / 2 }
  }

  const tocar = ev => {
    if (hubieArrastre.current) { hubieArrastre.current = false; return }
    const p = enMetros(ev)
    if (modo === 'toma') { setToma(p); return }
    if (modo === 'trazo') {
      if (cerrado) return          // ya cerrado: ahora se ajusta arrastrando, no agregando
      setPoligono([...poligono, p])
      return
    }
    if (modo === 'colocar') {
      if (cerrado && !dentro(p, poligono)) return
      const m = aspersorPorId(modeloActivo)
      setAspersores([...aspersores, {
        id: Math.random().toString(36).slice(2, 8),
        x: p.x, y: p.y, modelo: m.id,
        radio: m.radio, gasto: m.gasto, presion: m.presion, arco: 360, orientacion: 0
      }])
    }
  }

  const tomar = (ev, objetivo) => {
    ev.stopPropagation()
    setArrastrando(objetivo)
  }

  const mover = ev => {
    if (!arrastrando) return
    ev.preventDefault()
    hubieArrastre.current = true
    const p = enMetros(ev)
    if (arrastrando.tipo === 'aspersor') {
      setAspersores(aspersores.map(a => a.id === arrastrando.id ? { ...a, x: p.x, y: p.y } : a))
    } else if (arrastrando.tipo === 'vertice') {
      setPoligono(poligono.map((v, i) => i === arrastrando.i ? p : v))
    } else if (arrastrando.tipo === 'toma') {
      setToma(p)
    }
  }

  const soltar = () => setArrastrando(null)

  const cob = useMemo(
    () => cerrado ? cobertura(poligono, aspersores, 0.4) : null,
    [poligono, aspersores, cerrado]
  )

  const misLados = lados(poligono, cerrado)

  /* La retícula se dibuja en metros enteros dentro de la vista. */
  const lineas = []
  for (let x = Math.ceil(vista.x); x <= vista.x + vista.w; x++) lineas.push({ v: true, p: x })
  for (let y = Math.ceil(vista.y); y <= vista.y + vista.h; y++) lineas.push({ v: false, p: y })

  const editable = modo === 'trazo' || modo === 'colocar'

  return (
    <div className="lienzo-jardin">
      <svg
        ref={svgRef}
        viewBox={`${vista.x} ${vista.y} ${vista.w} ${vista.h}`}
        onClick={tocar}
        onTouchMove={mover}
        onTouchEnd={soltar}
        onMouseMove={mover}
        onMouseUp={soltar}
        onMouseLeave={soltar}
        role="application"
        aria-label="Plano del jardín"
      >
        <defs>
          {/* Lo seco: el jardín en rojo menos lo que alcanza el riego. Exacto, sin muestreo. */}
          <mask id="mascara-seco" maskUnits="userSpaceOnUse"
                x={vista.x} y={vista.y} width={vista.w} height={vista.h}>
            <path d={camino(poligono)} fill="#fff" />
            {aspersores.map(a => sector(a)
              ? <path key={a.id} d={sector(a)} fill="#000" />
              : <circle key={a.id} cx={a.x} cy={a.y} r={a.radio} fill="#000" />)}
          </mask>
          {/* Recorte al terreno. El agua que cae DENTRO es cobertura; la que cae fuera es
              desperdicio, y no puede pintarse igual: el porcentaje de la cota se mide solo
              adentro, así que dibujarlas del mismo tono contradice el número. */}
          {cerrado && <clipPath id="recorte-jardin"><path d={camino(poligono)} /></clipPath>}
        </defs>

        <g className="reticula">
          {lineas.map((l, i) => l.v
            ? <line key={i} x1={l.p} y1={vista.y} x2={l.p} y2={vista.y + vista.h} strokeWidth={u * .3} />
            : <line key={i} x1={vista.x} y1={l.p} x2={vista.x + vista.w} y2={l.p} strokeWidth={u * .3} />)}
        </g>

        {cerrado && <path className="terreno" d={camino(poligono)} />}

        {/* El agua que se sale del terreno: se ve, pero apagada. Es información —estás
            regando la banqueta del vecino— sin competir con la cobertura real. */}
        <g className="derrame">
          {aspersores.map(a => sector(a)
            ? <path key={`d-${a.id}`} d={sector(a)} />
            : <circle key={`d-${a.id}`} cx={a.x} cy={a.y} r={a.radio} />)}
        </g>
        {/* El agua que sí riega, recortada al terreno. */}
        <g className="mojado" clipPath={cerrado ? 'url(#recorte-jardin)' : undefined}>
          {aspersores.map(a => sector(a)
            ? <path key={`m-${a.id}`} d={sector(a)} />
            : <circle key={`m-${a.id}`} cx={a.x} cy={a.y} r={a.radio} />)}
        </g>

        {cerrado && aspersores.length > 0 && (
          <path className="seco" d={camino(poligono)} mask="url(#mascara-seco)" />
        )}

        {/* Borde */}
        {poligono.length > 1 && !cerrado && (
          <polyline className="borde" fill="none" strokeWidth={u * 1.4}
            points={poligono.map(p => `${p.x},${p.y}`).join(' ')} />
        )}
        {cerrado && <path className="borde" fill="none" strokeWidth={u * 1.4} d={camino(poligono)} />}

        {/* LA COTA DE CADA LADO. Es lo que permite comparar contra el flexómetro en el
            momento, en vez de descubrir el error al final. */}
        {misLados.map(l => {
          // La etiqueta se separa del lado hacia afuera, perpendicular al tramo.
          const dx = l.b.x - l.a.x, dy = l.b.y - l.a.y
          const n = Math.hypot(dx, dy) || 1
          const off = u * 2.6
          return (
            <g key={`c-${l.i}`} className="cota">
              <text
                x={l.medio.x + (-dy / n) * off}
                y={l.medio.y + (dx / n) * off}
                fontSize={tipo}
                textAnchor="middle" dominantBaseline="middle"
              >{l.largo.toFixed(1)} m</text>
            </g>
          )
        })}

        {/* Esquinas: se pueden arrastrar siempre que se esté trazando, también después de
            cerrar el polígono. El tirador transparente es más grande que el punto. */}
        {modo === 'trazo' && poligono.map((p, i) => (
          <g key={`v-${i}`} className={`vertice ${arrastrando?.tipo === 'vertice' && arrastrando.i === i ? 'activo' : ''}`}>
            <circle className="tirador" cx={p.x} cy={p.y} r={u * 4.5}
              onMouseDown={ev => tomar(ev, { tipo: 'vertice', i })}
              onTouchStart={ev => tomar(ev, { tipo: 'vertice', i })} />
            <circle className="punto" cx={p.x} cy={p.y} r={u * 1.4} strokeWidth={u * .5} />
          </g>
        ))}

        {/* Aspersores */}
        {aspersores.map(a => (
          <g key={a.id} className={`aspersor ${arrastrando?.id === a.id ? 'activo' : ''}`}>
            <circle className="tirador" cx={a.x} cy={a.y} r={u * 4.5}
              onMouseDown={ev => editable && tomar(ev, { tipo: 'aspersor', id: a.id })}
              onTouchStart={ev => editable && tomar(ev, { tipo: 'aspersor', id: a.id })} />
            <circle className="cabeza" cx={a.x} cy={a.y} r={u * 1.3} strokeWidth={u * .35} />
          </g>
        ))}

        {/* La toma, también movible */}
        {toma && (
          <g className="toma">
            <circle className="tirador" cx={toma.x} cy={toma.y} r={u * 4.5}
              onMouseDown={ev => tomar(ev, { tipo: 'toma' })}
              onTouchStart={ev => tomar(ev, { tipo: 'toma' })} />
            <circle cx={toma.x} cy={toma.y} r={u * 2.6} strokeWidth={u * .4} />
            <path strokeWidth={u * .5}
              d={`M${toma.x - u * 1.3} ${toma.y} h${u * 2.6} M${toma.x} ${toma.y - u * 1.3} v${u * 2.6}`} />
          </g>
        )}
      </svg>

      {/* Cota viva: lo que el dibujo mide AHORA, sin tener que calcular. */}
      <div className="cota-jardin">
        {cerrado
          ? <>
              <span><b>{area(poligono).toFixed(1)}</b> m² de jardín</span>
              <span><b>{perimetro(poligono).toFixed(1)}</b> m de perímetro</span>
              {aspersores.length > 0 && cob && (
                <span className={cob.porcentaje < 99 ? 'alerta' : 'bien'}>
                  <b>{cob.porcentaje.toFixed(0)}%</b> cubierto
                  {cob.porcentaje < 99 && ` · ${cob.seco.toFixed(1)} m² en rojo`}
                </span>
              )}
            </>
          : <span>
              {poligono.length === 0 && modo === 'trazo' && 'Toca una esquina del jardín para empezar.'}
              {poligono.length === 1 && 'Una esquina puesta. Faltan al menos dos más.'}
              {poligono.length === 2 && <>Llevas <b>{misLados[0]?.largo.toFixed(1)}</b> m y <b>{misLados[1]?.largo.toFixed(1)}</b> m. Falta una esquina para cerrar.</>}
              {poligono.length === 0 && modo !== 'trazo' && 'El jardín todavía no está trazado.'}
            </span>}
      </div>
    </div>
  )
}
